import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiPlus, FiRefreshCw, FiSave, FiShield, FiTrash2 } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { readStoredUser } from "../../../utils/sessionUser";
import { hasPurchasingSettingsPermission } from "../../../utils/purchasingSettingsPermissions";
import "./WhatsAppSendersTab.css";

const API = "/api/purchasing/whatsapp-delivery-notes/senders";
const COPY = {
  es: {
    title: "Remitentes de WhatsApp", subtitle: "Autoriza quién puede enviar albaranes a la unidad de negocio activa.",
    receiver: "Número receptor", configured: "Número receptor configurado", missing: "El administrador debe configurar primero el número receptor de esta BU.",
    routing: "Un receptor puede compartirse entre BUs. Cada combinación de receptor y remitente pertenece a una sola BU; el mismo remitente puede usar otro receptor para otra BU.",
    admin: "El número receptor y la conexión de Meta se configuran en el panel de administración.",
    status: "Integración", states: { active: "Activa", draft: "Borrador", disabled: "Desactivada", not_configured: "Sin configurar" },
    limit: "Máximo 500 remitentes por BU.",
    empty: "Sin remitentes activos guardados se descartan todos los documentos de esta BU antes de descargar archivos o iniciar OCR. No se autoriza automáticamente ningún número.",
    active: "activos", total: "en la lista", sender: "Remitente", phone: "Teléfono internacional", name: "Nombre (opcional)", enabled: "Activo", disabled: "Inactivo",
    format: "Incluye + y el prefijo del país de cada remitente: por ejemplo, +34666234478 para España. Puedes pegar espacios o guiones; se eliminan al guardar. Se admiten entre 7 y 15 dígitos y no se presupone ningún país.",
    add: "Añadir remitente", remove: "Eliminar remitente", save: "Guardar remitentes", saving: "Guardando…", refresh: "Recargar", loading: "Cargando remitentes…",
    saved: "Remitentes autorizados guardados.", dirty: "Hay cambios sin guardar. Guarda para aplicar las autorizaciones.",
    noUnit: "Selecciona una unidad de negocio para gestionar sus remitentes.", denied: "No tienes permiso para ver esta configuración.", readOnly: "Puedes consultar la lista. Necesitas permiso para modificar los remitentes de WhatsApp para editarla.",
    invalid: "Introduce un teléfono internacional válido con + y entre 7 y 15 dígitos.", duplicate: "Este teléfono ya aparece en la lista de esta BU.",
    loadFailed: "No se han podido cargar los remitentes. Vuelve a intentarlo.", saveFailed: "No se han podido guardar los remitentes.",
    conflict: "Este remitente ya está autorizado en otra BU para este número receptor. Usa otro receptor o elimina la asignación anterior.",
    unitChanged: "La unidad de negocio de la sesión ha cambiado. Recarga la página o selecciona la BU correcta antes de gestionar sus remitentes.",
  },
  en: {
    title: "WhatsApp senders", subtitle: "Authorize who can send delivery notes to the active Business Unit.",
    receiver: "Receiving number", configured: "Receiving number configured", missing: "An administrator must first configure this BU's receiving number.",
    routing: "A receiving number can be shared by BUs. Each receiver and sender combination belongs to one BU; the same sender can use another receiver for another BU.",
    admin: "The receiving number and Meta connection are configured in the admin panel.",
    status: "Integration", states: { active: "Active", draft: "Draft", disabled: "Disabled", not_configured: "Not configured" },
    limit: "Maximum 500 senders per BU.",
    empty: "Without active saved senders, all documents for this BU are discarded before downloading files or starting OCR. No number is authorized automatically.",
    active: "active", total: "in the list", sender: "Sender", phone: "International phone number", name: "Name (optional)", enabled: "Active", disabled: "Inactive",
    format: "Include + and each sender's country code: for example, +34666234478 for Spain. You can paste spaces or hyphens; they are removed when saving. Use 7–15 digits. No country is assumed.",
    add: "Add sender", remove: "Remove sender", save: "Save senders", saving: "Saving…", refresh: "Reload", loading: "Loading senders…",
    saved: "Authorized senders saved.", dirty: "There are unsaved changes. Save to apply sender authorization.",
    noUnit: "Select a Business Unit to manage its senders.", denied: "You do not have permission to view these settings.", readOnly: "You can view this list. Editing requires permission to update WhatsApp senders.",
    invalid: "Enter a valid international phone number with + and 7–15 digits.", duplicate: "This phone number already appears in this BU's list.",
    loadFailed: "Could not load senders. Please try again.", saveFailed: "Could not save senders.",
    conflict: "This sender is already authorized in another BU for this receiving number. Use another receiver or remove the previous assignment.",
    unitChanged: "The session's Business Unit has changed. Reload the page or select the correct BU before managing its senders.",
  },
};

function editableSenders(senders) {
  return Array.isArray(senders) ? senders.map((sender) => {
    const phone = String(sender.phone_number || "");
    return { phone_number: /^\d+$/.test(phone) ? `+${phone}` : phone, name: sender.name || "",
      active: sender.active !== false, ...(sender.user_id ? { user_id: sender.user_id } : {}) };
  }) : [];
}

function normalizePhone(value) {
  const phone = String(value || "").trim();
  if (!/^\+[\d\s().-]+$/.test(phone)) return null;
  const normalized = `+${phone.replace(/\D/g, "")}`;
  return /^\+[1-9]\d{6,14}$/.test(normalized) ? normalized : null;
}

function senderPayload(senders, copy) {
  const phones = new Set();
  return senders.map((sender, index) => {
    const phone = normalizePhone(sender.phone_number);
    if (!phone) throw new Error(`${copy.sender} ${index + 1}: ${copy.invalid}`);
    if (phones.has(phone)) throw new Error(`${copy.sender} ${index + 1}: ${copy.duplicate}`);
    phones.add(phone);
    return { ...sender, phone_number: phone, name: sender.name.trim() };
  });
}

export default function WhatsAppSendersTab() {
  const { languageId } = usePmsTheme();
  const copy = COPY[languageId] || COPY.en;
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(readStoredUser);
  // Without a URL BU, keep the displayed scope stable across other tabs' changes.
  const [displayedBusinessUnitId, setDisplayedBusinessUnitId] = useState(user?.active_business_unit_id);
  const businessUnitId = Number(searchParams.has("business_unit_id") ? searchParams.get("business_unit_id") : displayedBusinessUnitId);
  const unitMismatch = String(businessUnitId) !== String(user?.active_business_unit_id);
  const canView = hasPurchasingSettingsPermission(user, "whatsapp", "view");
  const canEdit = canView && hasPurchasingSettingsPermission(user, "whatsapp", "update");
  const context = `${user?.id}:${businessUnitId}:${user?.active_business_unit_id}:${canView}:${canEdit}`;
  const currentContext = useRef(context);
  currentContext.current = context;
  const requestVersion = useRef(0);
  const mutationLock = useRef(false);
  const [receiver, setReceiver] = useState(null);
  const [senders, setSenders] = useState([]);
  const [savedSenders, setSavedSenders] = useState([]);
  const [loadedContext, setLoadedContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    const update = () => {
      const session = readStoredUser();
      setUser(session); setDisplayedBusinessUnitId(session?.active_business_unit_id);
    };
    const updateFromOtherTab = () => setUser(readStoredUser());
    window.addEventListener("palantir-session-updated", update);
    window.addEventListener("storage", updateFromOtherTab);
    return () => {
      window.removeEventListener("palantir-session-updated", update);
      window.removeEventListener("storage", updateFromOtherTab);
    };
  }, []);

  const load = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoadedContext(null); setReceiver(null); setSenders([]); setSavedSenders([]); setError(""); setNotice("");
    if (!businessUnitId || !canView || unitMismatch) { setLoading(false); return; }
    setLoading(true);
    try {
      const response = await axios.get(API, { pilcoreExpectedUserId: user?.id, headers: { "X-Pilcore-Business-Unit-Id": businessUnitId } });
      if (requestVersion.current !== version || currentContext.current !== context) return;
      const session = readStoredUser();
      if (String(session?.active_business_unit_id) !== String(businessUnitId) || session?.id !== user?.id) { setUser(session); return; }
      if (response.data?.business_unit_id != null && Number(response.data.business_unit_id) !== businessUnitId) {
        setError(copy.unitChanged); return;
      }
      const next = editableSenders(response.data?.authorized_senders);
      setReceiver(response.data?.receiver || { status: "not_configured" });
      setSenders(next); setSavedSenders(next); setLoadedContext(context);
    } catch (err) {
      if (requestVersion.current === version) setError(err?.response?.data?.code === "WHATSAPP_BUSINESS_UNIT_CHANGED" ? copy.unitChanged : copy.loadFailed);
    } finally {
      if (requestVersion.current === version) setLoading(false);
    }
  }, [businessUnitId, canView, context, copy.loadFailed, copy.unitChanged, unitMismatch, user?.id]);

  useEffect(() => {
    load();
    return () => { requestVersion.current += 1; };
  }, [load]);

  const loaded = !unitMismatch && loadedContext === context;
  const configured = loaded && receiver?.status !== "not_configured";
  const dirty = JSON.stringify(senders) !== JSON.stringify(savedSenders);
  const activeCount = senders.filter((sender) => sender.active).length;
  const busy = saving || loading;

  const editSender = (index, field, value) => {
    setSenders((current) => current.map((sender, position) => {
      if (position !== index) return sender;
      const next = { ...sender, [field]: value };
      if (field === "phone_number" && normalizePhone(value) !== normalizePhone(sender.phone_number)) delete next.user_id;
      return next;
    }));
    setNotice("");
  };

  const save = async (event) => {
    event.preventDefault();
    if (mutationLock.current || busy || !canEdit || !configured || !dirty) return;
    const session = readStoredUser();
    if (String(session?.active_business_unit_id) !== String(businessUnitId) || session?.id !== user?.id) { setUser(session); return; }
    if (!hasPurchasingSettingsPermission(session, "whatsapp", "view") || !hasPurchasingSettingsPermission(session, "whatsapp", "update")) { setUser(session); return; }
    setError(""); setNotice("");
    const version = requestVersion.current;
    mutationLock.current = true; setSaving(true);
    try {
      const response = await axios.put(API, { authorized_senders: senderPayload(senders, copy) }, { pilcoreExpectedUserId: user?.id, headers: { "X-Pilcore-Business-Unit-Id": businessUnitId } });
      if (requestVersion.current !== version || currentContext.current !== context) return;
      const currentSession = readStoredUser();
      if (String(currentSession?.active_business_unit_id) !== String(businessUnitId) || currentSession?.id !== user?.id) { setUser(currentSession); return; }
      if (response.data?.business_unit_id != null && Number(response.data.business_unit_id) !== businessUnitId) {
        throw Object.assign(new Error(), { response: { data: { code: "WHATSAPP_BUSINESS_UNIT_CHANGED" } } });
      }
      const next = editableSenders(response.data?.authorized_senders);
      setReceiver(response.data?.receiver || receiver);
      setSenders(next); setSavedSenders(next); setNotice(copy.saved);
    } catch (err) {
      if (requestVersion.current !== version || currentContext.current !== context) return;
      if (err?.response?.data?.code === "WHATSAPP_BUSINESS_UNIT_CHANGED") {
        setLoadedContext(null); setReceiver(null); setSenders([]); setSavedSenders([]); setError(copy.unitChanged);
      } else if (err?.response?.data?.code === "WHATSAPP_RECEIVER_NOT_CONFIGURED") setError(copy.missing);
      else if (err?.response?.status === 409) setError(copy.conflict);
      else setError(err?.response ? copy.saveFailed : err.message || copy.saveFailed);
    } finally {
      mutationLock.current = false; setSaving(false);
    }
  };

  return <section className="waSenders" aria-labelledby="wa-senders-title">
    <header className="waSenders__header"><div><h1 id="wa-senders-title"><FaWhatsapp aria-hidden="true" />{copy.title}</h1><p>{copy.subtitle}</p></div>
      {canView && Boolean(businessUnitId) && <button type="button" disabled={busy || unitMismatch} onClick={load}><FiRefreshCw aria-hidden="true" />{copy.refresh}</button>}
    </header>
    {!canView ? <p className="waSenders__notice" role="alert">{copy.denied}</p> : !businessUnitId ? <p className="waSenders__notice">{copy.noUnit}</p> : unitMismatch ? <p className="waSenders__notice waSenders__notice--error" role="alert">{copy.unitChanged}</p> : <form onSubmit={save}>
      <div className="waSenders__body">
        {loading && <p role="status">{copy.loading}</p>}
        {error && <p className="waSenders__notice waSenders__notice--error" role="alert">{error}</p>}
        {loaded && <>
          <div className="waSenders__receiver"><FiShield aria-hidden="true" /><div><strong>{copy.receiver}: {configured ? receiver?.display_phone_number || copy.configured : "—"}</strong><p>{copy.status}: {copy.states[receiver?.status] || receiver?.status}</p><p>{configured ? copy.admin : copy.missing}</p></div></div>
          <p>{copy.routing}</p>
          <div className="waSenders__summary"><strong>{activeCount} {copy.active} · {senders.length} {copy.total}</strong></div>
          {!savedSenders.some((sender) => sender.active) && <p className="waSenders__notice" role="status">{copy.empty}</p>}
          {!canEdit && <p className="waSenders__notice">{copy.readOnly}</p>}
          <fieldset disabled={!canEdit || busy || !configured}>
            {senders.map((sender, index) => <div className="waSenders__card" key={index} role="group" aria-label={`${copy.sender} ${index + 1}`}>
              <label><span>{copy.phone}</span><input type="tel" inputMode="tel" autoComplete="off" aria-label={`${copy.phone} ${index + 1}`} aria-describedby="wa-sender-format"
                placeholder="+34666234478" value={sender.phone_number} onChange={(event) => editSender(index, "phone_number", event.target.value)} /></label>
              <label><span>{copy.name}</span><input autoComplete="off" maxLength={120} aria-label={`${copy.name} ${index + 1}`} value={sender.name} onChange={(event) => editSender(index, "name", event.target.value)} /></label>
              <div className="waSenders__cardActions"><label className="waSenders__toggle"><input type="checkbox" aria-label={`${copy.enabled} ${index + 1}`} checked={sender.active}
                onChange={(event) => editSender(index, "active", event.target.checked)} /><span>{sender.active ? copy.enabled : copy.disabled}</span></label>
                <button type="button" className="waSenders__remove" aria-label={`${copy.remove} ${index + 1}`}
                  onClick={() => { setSenders((current) => current.filter((_sender, position) => position !== index)); setNotice(""); }}><FiTrash2 aria-hidden="true" />{copy.remove}</button></div>
            </div>)}
            <p className="waSenders__hint" id="wa-sender-format">{copy.format}</p>
            <button type="button" disabled={senders.length >= 500} onClick={() => { setSenders((current) => [...current, { phone_number: "", name: "", active: true }]); setNotice(""); }}><FiPlus aria-hidden="true" />{copy.add}</button>
            {senders.length >= 500 && <p className="waSenders__hint" role="status">{copy.limit}</p>}
          </fieldset>
        </>}
      </div>
      <footer className="waSenders__footer"><p role="status">{notice || (dirty ? copy.dirty : "")}</p>
        {canEdit && <button type="submit" className="waSenders__primary" disabled={busy || !configured || !dirty}><FiSave aria-hidden="true" />{saving ? copy.saving : copy.save}</button>}
      </footer>
    </form>}
  </section>;
}
